import React from 'react';
import {
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    Legend,
    Pie,
    PieChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis
} from 'recharts';
import { accountingOversightResponse } from '../../../models/entities/businessPartner/accountingOversight';
import { formatMoney } from '../businessPartners/singleBusinessPartner/sections/Accounting/format';
import { ChartCard, ChartTitle, ChartsGrid } from './AccountingOversightStyles';

type Props = {
    data: accountingOversightResponse;
    currency: string;
};

const AGING_COLORS: Record<string, string> = {
    current: '#22c55e',
    d31_60: '#eab308',
    d61_90: '#f97316',
    d91plus: '#ef4444'
};

const OversightCharts: React.FC<Props> = ({ data, currency }) => {
    const fmt = (v: number) => formatMoney(v, currency);

    const agingData = data.collection.agingBuckets.map((b) => ({
        name: b.label.replace(' (0–30)', '').replace(' Days', 'd'),
        amount: b.amount,
        count: b.count,
        key: b.key
    }));

    const billingData = [
        { name: 'Ready', count: data.billingControl.readyForBilling, fill: '#639e31' },
        { name: 'Vendor', count: data.billingControl.waitingVendorInvoice, fill: '#c1292e' },
        { name: 'Customs', count: data.billingControl.waitingCustomsCharges, fill: '#00B5B9' },
        { name: 'Transport', count: data.billingControl.waitingTransportInvoice, fill: '#f4c915' },
        { name: 'Ops', count: data.billingControl.waitingOpsApproval, fill: '#a56b1c' }
    ];

    const cashFlowData = [
        { name: 'Collection', value: data.cashFlow.expectedCollection, fill: '#15803d' },
        { name: 'Payments', value: data.cashFlow.expectedPayments, fill: '#b91c1c' },
        { name: 'VAT/Tax', value: data.cashFlow.vatAndTaxes, fill: '#64748b' }
    ];

    return (
        <ChartsGrid>
            <ChartCard>
                <ChartTitle>Collection aging</ChartTitle>
                <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={agingData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#ececec" />
                        <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#6b7280' }} />
                        <YAxis tick={{ fontSize: 11, fill: '#6b7280' }} width={48} />
                        <Tooltip
                            formatter={(value: number) => fmt(value)}
                            contentStyle={{ borderRadius: 8, border: '1px solid #ececec', fontSize: 12 }}
                        />
                        <Bar dataKey="amount" radius={[4, 4, 0, 0]}>
                            {agingData.map((entry) => (
                                <Cell key={entry.key} fill={AGING_COLORS[entry.key] ?? '#94a3b8'} />
                            ))}
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
            </ChartCard>

            <ChartCard>
                <ChartTitle>Billing pipeline (files)</ChartTitle>
                <ResponsiveContainer width="100%" height={200}>
                    <BarChart layout="vertical" data={billingData} margin={{ top: 4, right: 12, left: 4, bottom: 4 }}>
                        <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#ececec" />
                        <XAxis type="number" tick={{ fontSize: 11, fill: '#6b7280' }} />
                        <YAxis type="category" dataKey="name" width={56} tick={{ fontSize: 11, fill: '#6b7280' }} />
                        <Tooltip contentStyle={{ borderRadius: 8, border: '1px solid #ececec', fontSize: 12 }} />
                        <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                            {billingData.map((entry) => (
                                <Cell key={entry.name} fill={entry.fill} />
                            ))}
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
            </ChartCard>

            <ChartCard>
                <ChartTitle>Cash flow mix</ChartTitle>
                <ResponsiveContainer width="100%" height={200}>
                    <PieChart>
                        <Pie
                            data={cashFlowData}
                            dataKey="value"
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            innerRadius={48}
                            outerRadius={72}
                            paddingAngle={2}
                        >
                            {cashFlowData.map((entry) => (
                                <Cell key={entry.name} fill={entry.fill} />
                            ))}
                        </Pie>
                        <Tooltip formatter={(value: number) => fmt(value)} />
                        <Legend wrapperStyle={{ fontSize: 12 }} />
                    </PieChart>
                </ResponsiveContainer>
                <div style={{ textAlign: 'center', fontSize: 13, fontWeight: 600, color: data.cashFlow.forecastBalance >= 0 ? '#15803d' : '#b91c1c' }}>
                    Forecast balance: {fmt(data.cashFlow.forecastBalance)}
                </div>
            </ChartCard>
        </ChartsGrid>
    );
};

export default OversightCharts;
